import React, { useCallback } from "react";
import { toastSuccess, toastError } from "../../../utils";
import { LocalityContext } from "../../../context/localityContext";

import _ from "lodash";

import "./Locality.scss";
import ScriptBox from "../../../components/ScriptBox/ScriptBox";
import EditIcon from "../../../components/VectorIcons/EditIcon";
import BoxFooter from "../../../components/BoxFooter/BoxFooter";
import Button from "../../../components/Button/Button";
import CheckBox from "../../../components/CheckBox/CheckBox";
import Creatable from "react-select/creatable";
import LocalityOption from "../../../components/LocalityFieldset/LocalityOption/LocalityOption";
import { Alert } from "./Alert/Alert";

import { getService, postService } from "../../../services/axios/services";
import { CustomDropdownIndicator } from "../../../components/DropdownIndicator/CustomDropdownIndicator";

export function Locality() {
  const {
    type,
    fields,
    setField,
    setClearFieldAndItsChildren,
    localities,
    isModalOpen,
    setToggleModalOpen,
    setFieldIsFictious,
    setClearFormData,
    setRequestedLocalitiesData,
    setLastForm,
    setLastCreation,
    selectedIdToEdit,
  } = React.useContext(LocalityContext);

  const initialInputValues = {
    Continente: "",
    Região: "",
    País: "",
    Estado: "",
    Cidade: "",
    Bairro: "",
  };

  const [inputValues, setInputValues] = React.useState(initialInputValues);
  const [isSubmiting, setIsSubmiting] = React.useState(false);
  const [isValidating, setIsValidating] = React.useState(false);
  const [showAlert, setShowAlert] = React.useState(false);
  const [lastChangedField, setLastChangedField] = React.useState(null);
  const [canSave, setCanSave] = React.useState(false);
  const [autoCompletedForEdit, setAutoCompletedForEdit] = React.useState(false);
  const [immutableFields] = React.useState(fields);

  const clearAll = () => {
    setIsSubmiting(false);
    setIsValidating(false);
    setShowAlert(false);
    setLastChangedField(null);
    setCanSave(false);
    setAutoCompletedForEdit(false);
    setRequestedLocalitiesData(null);
    setInputValues(initialInputValues);
  };

  function handleAllowedEditFields(array, field) {
    const index = array.findIndex((item) => item.field === field);
    return index !== -1 ? array.slice(index) : [];
  }

  const fieldsList = Object.values(fields);
  const currentEditingField = fieldsList.find(
    (field) => field.id === selectedIdToEdit
  )?.field;
  const currentEditingFieldIndex = fieldsList.findIndex(
    (field) => field.id === selectedIdToEdit
  );

  const fieldsToMap =
    type === "REGISTER"
      ? fieldsList
      : handleAllowedEditFields(fieldsList, currentEditingField);

  const handleFictiousChange = (type) => {
    setFieldIsFictious(type);
    setLastChangedField(type);
  };

  const hasChangesOnEdit = !_.isEqual(immutableFields, fields);

  const canSaveRegister = fieldsToMap?.some((field) => field.id || field.name);

  const canSaveEdit = hasChangesOnEdit;

  const clearParentFields = useCallback(
    (key) => {
      const hierarchy = [
        "Bairro",
        "Cidade",
        "Estado",
        "Região",
        "País",
        "Continente",
      ];

      const keyIndex = hierarchy.indexOf(key);

      for (let i = keyIndex + 1; i < hierarchy.length; i++) {
        const parentKey = hierarchy[i];
        setField({
          field: parentKey,
          id: null,
          name: "",
          parentId: null,
          isFictional: false,
          isAutocomplete: true,
        });
      }
    },
    [setField]
  );

  const autocompleteFieldsBySelected = useCallback(
    ({ selected, includeSelf }) => {
      if (!selected.parentId && !includeSelf && !selected.id) return;
      if (!selected.parentId && !includeSelf)
        return clearParentFields(selected.type || selected.field);

      const hierarchy = [];

      if (includeSelf) hierarchy.push(selected);

      let current = selected;

      const checkByParentId = (item) => item.id === current.parentId;

      while (current && current.parentId) {
        const parent = localities.find(checkByParentId);
        if (!parent) break;

        hierarchy.push(parent);
        current = parent;
      }

      hierarchy.forEach((foundParent) => {
        setField({
          field: foundParent.type,
          id: foundParent.id,
          name: foundParent.name,
          parentId: foundParent.parentId || null,
          isFictional: foundParent.isFictional,
          isAutocomplete: true,
        });
      });
    },
    [setField, localities, clearParentFields]
  );

  const handleChangeLocality = (selectedOption, field) => {
    setLastChangedField(field);

    if (!selectedOption) {
      return setClearFieldAndItsChildren({
        field,
      });
    }

    setField({
      field: field,
      id: selectedOption.id || null,
      name: selectedOption.name,
      parentId: selectedOption.parentId || null,
      value: selectedOption.value || null,
      isFictional: selectedOption.isFictional || false,
    });

    autocompleteFieldsBySelected({ selected: selectedOption });
  };

  const handleOnCreateOption = (inputValue, field) => {
    const newOption = {
      id: null,
      isFictional: fields[field].isFictional,
      field,
      name: inputValue,
      label: inputValue,
      value: inputValue,
    };
    handleChangeLocality(newOption, field);
  };

  const handleInputChange = (inputValue, action, field) => {
    if (
      action === "input-change" &&
      type === "EDIT" &&
      field === currentEditingField
    ) {
      const foundOption = localities.find(
        (option) => option.id === selectedIdToEdit
      );

      handleChangeLocality({ ...foundOption, name: inputValue }, field);

      setInputValues((prev) => ({
        ...prev,
        [field]: inputValue,
      }));

      return;
    }

    if (!["menu-close", "input-blur", "set-value"].includes(action)) {
      setInputValues((prev) => ({
        ...prev,
        [field]: inputValue,
      }));

      return;
    }
  };

  const handleFocus = (value, field) => {
    if (value) {
      setInputValues((prev) => ({
        ...prev,
        [field]: value.name,
      }));
    }
  };

  const addParentHierarchyIntoLocality = useCallback(
    (locality, localitiesResponse) => {
      if (!locality.parentId) return locality;

      const foundParent = localitiesResponse.find(
        (item) => item.id === locality.parentId
      );

      return {
        ...locality,
        parentHierarchy: foundParent.name,
      };
    },
    []
  );

  const handleAutoCompleteForEdit = useCallback(() => {
    if (
      !autoCompletedForEdit &&
      isModalOpen &&
      type === "EDIT" &&
      localities?.length &&
      selectedIdToEdit
    ) {
      autocompleteFieldsBySelected({
        selected: localities?.find((item) => item.id === selectedIdToEdit),
        includeSelf: true,
      });

      setAutoCompletedForEdit(true);
    }
  }, [
    autoCompletedForEdit,
    isModalOpen,
    type,
    localities,
    selectedIdToEdit,
    autocompleteFieldsBySelected,
  ]);

  const getLocalities = React.useCallback(() => {
    getService({ endpoint: `location/filter` })
      .then((response) => {
        const mappedResponse = response.data.map((locality, _, array) =>
          addParentHierarchyIntoLocality(locality, array)
        );
        const sortedResponse = _.sortBy(mappedResponse, [
          "name",
          (item) => item.isFictional,
        ]);
        setRequestedLocalitiesData(sortedResponse);
      })
      .catch((e) => {
        toastError("Erro ao carregar lista de localidades!");
      });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setRequestedLocalitiesData, addParentHierarchyIntoLocality]);

  const handleCreateNewLocality = (event) => {
    event.preventDefault();

    setIsSubmiting(true);

    setLastForm({ ...fields, type });

    const validFieldsToPost = Object.values(fields).filter(
      (field) => field.id || field.name
    );

    const handleIdForCreation = (locality) => {
      const found = localities.find(
        (localityItem) =>
          locality.id === localityItem.id &&
          localityItem.name === locality.name &&
          localityItem.isFictional !== locality.isFictional
      );

      return found ? null : locality.id;
    };

    const postContract = validFieldsToPost.map((field) => ({
      name: field.name,
      type: field.field,
      isFictional: field.isFictional,
      id: handleIdForCreation(field),
    }));

    postService({
      endpoint: `location/CreateAndUpdate`,
      postData: postContract,
    })
      .then((response) => {
        setIsSubmiting(false);
        setToggleModalOpen();
        toastSuccess("Localidade cadastrada com sucesso!");
        setLastCreation(response.data);
      })
      .catch((e) => {
        setIsSubmiting(false);
        toastError("Erro ao cadastradar localidade!");
      });
  };

  function isInputValueMatchingOption(inputValueStr, optionStr) {
    const normalize = (str) =>
      str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

    const normalizedA = normalize(inputValueStr);
    const normalizedB = normalize(optionStr);

    return (
      normalizedB.includes(normalizedA) &&
      normalizedA.length === normalizedB.length
    );
  }

  const handleEditLocality = (event) => {
    event.preventDefault();

    setIsSubmiting(true);

    setLastForm({ ...fields, type });

    const validFieldsToPost = Object.values(fields).filter(
      (field) => field.id || field.name
    );

    const postContract = validFieldsToPost.map((field) => ({
      name: field.name,
      type: field.field,
      isFictional: field.isFictional,
      id: field.id,
    }));

    postService({
      endpoint: `location/CreateAndUpdate`,
      postData: postContract,
    })
      .then((response) => {
        setIsSubmiting(false);
        setLastCreation(response.data);
        setToggleModalOpen();
        toastSuccess("Localidade editada com sucesso!");
      })
      .catch((e) => {
        toastError("Erro ao editar localidade!");
      });
  };

  const isSubmitValid = () => {
    if (isSubmiting) return false;
    if (isValidating) return false;
    if (type === "REGISTER" && canSaveRegister) return true;
    if (type === "EDIT" && canSaveEdit) return true;
  };

  const checkIfLocalityAlreadyExists = useCallback(
    (locality) => {
      return localities.some((localityItem) => {
        if (!localityItem.id && !localityItem.parentId) return false;
        return (
          localityItem.name === locality.name &&
          localityItem.type === locality.field &&
          localityItem.isFictional === locality.isFictional
        );
      });
    },
    [localities]
  );

  const checkIfSameHierarchyExists = useCallback(() => {
    setIsValidating(true);

    const localTree = Object.entries(fields)
      .filter(([_, value]) => value.name)
      .map(([_, value]) => ({
        id: value.id,
        name: value.name,
        isFictional: value.isFictional,
        type: value.field,
        parentId: value.parentId,
      }));

    const lowerTreeNode = localTree[0];

    if (!lowerTreeNode) {
      setIsValidating(false);
      setShowAlert(null);
      return;
    }

    getService({
      endpoint: `location/filter`,
      params: { name: lowerTreeNode.name },
    })
      .then(async (response) => {
        if (!response?.data) return;

        const hierarchiesFound = await Promise.all(
          response.data?.map(({ id }) => {
            return getService({ endpoint: `location/${id}/hierarchy` });
          })
        )
          .then((response) => {
            return response?.map((item) => item.data);
          })
          .catch(() => {
            setIsValidating(false);
            toastError("Erro ao verificar hierarquia da localidade!");
          });

        function fillNullParentIds(array) {
          return array.map((item, index) => {
            if (item.parentId === null && index + 1 < array.length) {
              return { ...item, parentId: array[index + 1].id };
            }
            return item;
          });
        }

        function removeIds(input) {
          if (Array.isArray(input[0])) {
            return input.map((innerArray) =>
              innerArray.map(({ id, parent, ...rest }) => rest)
            );
          } else {
            return input.map(({ id, parent, ...rest }) => rest);
          }
        }

        const isSameHierarchy = hierarchiesFound.find((hierarchy) => {
          return _.isEqual(
            removeIds(fillNullParentIds(localTree)),
            removeIds(hierarchy)
          );
        });

        const uniqueItemAlreadyExistsForType =
          localTree.length === 1 && checkIfLocalityAlreadyExists(lowerTreeNode);

        const isError = uniqueItemAlreadyExistsForType || isSameHierarchy;

        setCanSave(!isError);

        setShowAlert(isError && lastChangedField);

        setIsValidating(false);
      })
      .catch((e) => {
        setIsValidating(false);
        toastError("Erro ao verificar hierarquia da localidade!");
      });

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, lastChangedField, checkIfLocalityAlreadyExists]);

  const handleAlertMessageByField = (field) => {
    const types = {
      Bairro: "um bairro",
      Cidade: "uma cidade",
      Estado: "um estado",
      Região: "uma região",
      País: "um país",
      Continente: "um continente",
    };

    return types[field];
  };

  const handleDisabledFieldAlert = (field) => {
    setShowAlert(field);
  };

  const isCreatingNewLocality =
    type === "REGISTER" &&
    Object.values(fields).some((field) => field.name && !field.id);

  const fictionalFlagGAClassnames = (field) =>
    type === "REGISTER"
      ? fields[field].isFictional
        ? "ga-is-fictional-register"
        : "ga-is-not-fictional-register"
      : fields[field].isFictional
        ? "ga-is-fictional-edit"
        : "ga-is-not-fictional-edit";

  const saveButtonClassnamesForGA =
    type === "REGISTER"
      ? canSave &&
        isSubmitValid() &&
        isCreatingNewLocality &&
        "ga-register-new-locality"
      : canSave && isSubmitValid() && "ga-edit-locality";

  React.useEffect(() => {
    if (!isModalOpen) {
      setClearFormData();
      clearAll();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, setClearFormData]);

  React.useEffect(() => {
    if (isModalOpen) getLocalities();
  }, [isModalOpen, getLocalities]);

  React.useEffect(() => {
    if (!isModalOpen) return;

    const handler = setTimeout(() => {
      checkIfSameHierarchyExists();
    }, 850);

    return () => {
      clearTimeout(handler);
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValues, fields, checkIfSameHierarchyExists]);

  React.useEffect(() => {
    handleAutoCompleteForEdit();
  }, [handleAutoCompleteForEdit]);

  if (!isModalOpen) return null;

  return (
    <ScriptBox
      open={isModalOpen}
      clear={setToggleModalOpen}
      title={type === "REGISTER" ? "Cadastrar Localidade" : "Editar Localidade"}
      icon={<EditIcon color="#FFF" />}
      removePadding={true}
      marginBottom={true}
    >
      <div className="localityForm">
        {fieldsToMap?.map(({ field, label }) => {
          const options = localities?.filter(
            (locality) => locality.type === field
          );
          const currentValue =
            fields[field]?.id || fields[field]?.value ? fields[field] : null;

          const isCurrentEditing = fields[field].id === selectedIdToEdit;

          const isFictionalDisabled =
            (type === "EDIT" &&
              fields[field].id !== selectedIdToEdit &&
              fields[field].name) ||
            (type === "REGISTER" && fields[field].isAutocomplete) ||
            (type === "REGISTER" && fields[field].id);

          const isMenuOpen =
            type === "EDIT" && fields[field].id === selectedIdToEdit
              ? false
              : undefined;

          const inputValue =
            type === "EDIT" && fields[field].id === selectedIdToEdit
              ? inputValues[field]
              : undefined;

          const isDisabledDefault =
            (!isCurrentEditing && fields[field].isAutocomplete) ||
            (type === "REGISTER" && fields[field].isAutocomplete);

          const parentOfSelectedToEdit =
            Object.entries(fields)[currentEditingFieldIndex + 1]?.[1];
          const isParentOfSelectedToEdit =
            type === "EDIT" && parentOfSelectedToEdit.field === label;

          const isDisabled =
            type === "EDIT"
              ? isDisabledDefault && !isParentOfSelectedToEdit
              : isDisabledDefault;

          const showDropdownIndicator =
            options &&
            options.length > 0 &&
            fields[field].id !== selectedIdToEdit &&
            !isParentOfSelectedToEdit;

          const isFieldClearable =
            type === "EDIT" && fields[field].id === selectedIdToEdit
              ? false
              : isParentOfSelectedToEdit
                ? true
                : fields[field].isAutocomplete
                  ? false
                  : true;

          const isValidNewOption = (inputValue, _, options) => {
            if (type === "EDIT") return false;

            const foundOption = options?.find(
              (option) =>
                isInputValueMatchingOption(inputValue, option.name) &&
                !option.parentId
            );

            return !!inputValue && !foundOption;
          };

          return (
            <div key={field}>
              <div className="localityField">
                <CheckBox
                  value={field}
                  label="Fictício"
                  isActive={fields[field].isFictional}
                  isDisabled={isFictionalDisabled}
                  customEvent={handleFictiousChange}
                  GAEventClassNames={fictionalFlagGAClassnames(field)}
                />

                <div
                  className={`${
                    showAlert === field
                      ? "localityContainer alert"
                      : "localityContainer"
                  }`}
                >
                  <div className="localityName">{label}</div>

                  <div className="localitySeparator" />

                  <div className="localitySelector">
                    <div
                      className="selector"
                      onClick={() =>
                        isDisabled && handleDisabledFieldAlert(label)
                      }
                    >
                      <Creatable
                        className="reactSelect"
                        classNamePrefix="reactSelect"
                        isLoading={
                          !localities ||
                          (isValidating && lastChangedField === field)
                        }
                        loadingMessage={() => `Carregando opções de ${field}`}
                        isClearable={isFieldClearable}
                        options={options}
                        menuIsOpen={isMenuOpen}
                        value={currentValue}
                        getOptionValue={(option) => option.id || option.value}
                        getOptionLabel={(option) => option.name || ""}
                        onChange={(value) => handleChangeLocality(value, field)}
                        onCreateOption={(inputValue) =>
                          handleOnCreateOption(inputValue, field)
                        }
                        placeholder="Digite um local ou selecione da lista"
                        formatCreateLabel={(inputValue) =>
                          `${"Criar opção:"} "${inputValue}"`
                        }
                        noOptionsMessage={() => "Nenhuma opção encontrada"}
                        onInputChange={(inputValue, { action }) =>
                          handleInputChange(inputValue, action, field)
                        }
                        inputValue={inputValue}
                        onFocus={() =>
                          fields[field].id === selectedIdToEdit &&
                          handleFocus(currentValue, field)
                        }
                        blurInputOnSelect={true}
                        createOptionPosition={
                          type === "REGISTER" ? "last" : "first"
                        }
                        isValidNewOption={(inputValue, _, options) =>
                          isValidNewOption((inputValue, _, options))
                        }
                        isDisabled={isDisabled}
                        components={{
                          Option: LocalityOption,
                          DropdownIndicator: showDropdownIndicator
                            ? CustomDropdownIndicator
                            : () => null,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {showAlert === field && !isDisabled && (
                <Alert
                  message={
                    fields[field].name ? (
                      type === "REGISTER" ? (
                        <>
                          Local já cadastrado, caso deseje atualizar a
                          localidade acesse <strong>Editar</strong>
                        </>
                      ) : (
                        <>
                          Já existe um local cadastrado com o mesmo nome,
                          confira as informações ou altere algum campo
                        </>
                      )
                    ) : type === "EDIT" ? (
                      <>O campo não pode estar vazio</>
                    ) : (
                      <>
                        Digite {handleAlertMessageByField(field)} desejado para
                        cadastro, ou acesse
                        <strong> Editar </strong> para atualizar a localidade
                      </>
                    )
                  }
                />
              )}

              {showAlert === field && isDisabled && (
                <Alert
                  message={
                    type === "REGISTER" ? (
                      <>
                        Não é possível mudar uma associação já existente durante
                        a criação de localidade.
                      </>
                    ) : (
                      <>
                        Não é possível mudar uma associação já existente durante
                        a edição de localidade.
                      </>
                    )
                  }
                />
              )}
            </div>
          );
        })}
      </div>

      <BoxFooter>
        <Button
          text={isSubmiting ? "Salvando..." : "Salvar"}
          toValidate={true}
          isValidForm={canSave && isSubmitValid()}
          classButton="buttonGreen large"
          typeButton="submit"
          clickEvent={
            type === "REGISTER" ? handleCreateNewLocality : handleEditLocality
          }
          GAEventClassNames={`${saveButtonClassnamesForGA}`}
        />
      </BoxFooter>
    </ScriptBox>
  );
}
